import {AiChatProTipType} from './ai-chat-pro-tip-type';

export interface ProTipConfig {
  type: AiChatProTipType;
  icon: string;
  messages: string[];
  priority: number;
  triggerKeywords?: string[];
  /**
   * When true, the tip is never suppressed by the "seen" state — it fires
   * whenever its trigger criteria are met, subject only to PRO_TIP_COOLDOWN_MS.
   * Use for recurring problems we always want to nudge the user about (e.g.
   * long-chat context loss, referring to an older version).
   */
  persistent?: boolean;
}

const LOW_RES_THRESHOLD = 256;

export const PRO_TIP_COOLDOWN_MS = 30000;

export const PRO_TIP_CONFIGS: ProTipConfig[] = [
  {
    type: AiChatProTipType.ATTACHMENT_ADDED,
    icon: 'icon-paperclip',
    priority: 1,
    messages: [
      'pmwjs_protip_attachment_specify',
      'pmwjs_protip_attachment_refer',
      'pmwjs_protip_attachment_clear_images',
    ],
  },
  {
    type: AiChatProTipType.LOW_RES_ATTACHMENT,
    icon: 'icon-hd',
    priority: 2,
    messages: ['pmwjs_protip_low_res'],
  },
  {
    type: AiChatProTipType.REFER_OLDER_VERSION,
    icon: 'icon-paperclip',
    priority: 3,
    messages: [
      'pmwjs_protip_reattach_older',
      'pmwjs_protip_edit_older_messages',
    ],
    triggerKeywords: ['go back', 'previous design', 'original design', 'return to'],
    persistent: true,
  },
  {
    type: AiChatProTipType.EXACT_TEXT_PROMPTING,
    icon: 'icon-message-prompt',
    priority: 4,
    messages: [
      'pmwjs_protip_text_use_quotes',
      'pmwjs_protip_text_add_exact',
    ],
    triggerKeywords: ['text', 'font', 'title', 'heading', 'headline', 'subtitle', 'caption', 'slogan', 'tagline', 'word', 'letter', 'write', 'typing'],
  },
  {
    type: AiChatProTipType.SPELLING_ISSUES,
    icon: 'icon-spellcheck',
    priority: 5,
    messages: ['pmwjs_protip_spelling'],
    triggerKeywords: [
      'spelling',
      'spellings',
      'misspelled',
      'correct spelling',
      'correct spellings',
      'spelling error',
      'spelling errors',
    ],
  },
  {
    type: AiChatProTipType.LONG_CHAT_CONTEXT,
    icon: 'icon-new-chat',
    priority: 6,
    messages: [
      'pmwjs_protip_long_chat_new',
      'pmwjs_protip_long_chat_fresh',
    ],
    persistent: true,
  },
  {
    type: AiChatProTipType.VAGUE_PROMPT,
    icon: 'icon-library',
    priority: 7,
    messages: ['pmwjs_protip_vague_prompt'],
  },
  {
    type: AiChatProTipType.TOO_LONG_TEXT,
    icon: 'icon-scroll-text',
    priority: 8,
    messages: ['pmwjs_protip_too_long_text'],
  },
  {
    type: AiChatProTipType.EDITING_TIPS,
    icon: 'icon-zoom-in',
    priority: 9,
    messages: [
      'pmwjs_protip_editing_describe',
      'pmwjs_protip_editing_location',
      'pmwjs_protip_editing_exact_changes',
    ],
  },
];

const PERSISTENT_TIP_TYPES = new Set<AiChatProTipType>(PRO_TIP_CONFIGS.filter((config) => config.persistent === true).map((config) => config.type));

export function isPersistentTip(type: AiChatProTipType): boolean {
  return PERSISTENT_TIP_TYPES.has(type);
}

export {LOW_RES_THRESHOLD};
